/**
 * @Copyright 2014-2019 DataVoke, Inc. All rights reserved.
 */

'use strict';

import {
    EntityTypes,
    EntityStates
} from '../utils/dvtypes';
import * as tools from '../utils/tools';
import { applyClientProperties as applyPCClientProps} from '../cache/pendingChanges';
import { applyClientProperties as applyStyleClientProps } from '../cache/styles';
import { applyClientProperties as applyViewClientProps } from '../cache/views';
import { applyClientProperties as applyVSClientProps } from '../cache/valueStatuses';
import { applyClientProperties as applyTagClientProps } from '../cache/tags';
import { applyClientProperties as applySGClientProps } from '../cache/securityGroups';
import { applyClientProperties as applyPackageClientProps } from '../cache/packages';
import {recordTypes} from "../cache/recordTypes";
import {categories} from '../cache/categories';
import {properties} from '../cache/properties';
import {
    applyActionClientProperties,
    applyEndpointClientProperties,
    applyExpressionClientProperties
} from '../cache/extensions';

export default {
    /**
     * Fired when a designer commits an entity change.  Will incorporate these changes into the local cache and post a notification to the console...EK
     * @param event - The event that was sent by the service - it includes the entities that changed and the designer who made the changes...EK
     */
    entitiesUpdated(event) {
        let c = app.dv.cache;

        let mergeEntitiesToCache = (entities, cacheRef, entityType, postNewCallback = tools.noop, postUpdateCallback = tools.noop) => {
            entities.forEach((updatedEntity) => {
                let cachedEntity = cacheRef.getVal(updatedEntity),
                    actionString = "updated";
                if (cachedEntity) {
                    if(updatedEntity._State === EntityStates.Deleted) {
                        // Only delete pending changes for now until scenarios where someone else has the entity open has been thought through...AM
                        if (updatedEntity.PendingChangeID) {
                            cacheRef.remove(updatedEntity);
                        }
                        actionString = 'removed';
                    }
                    else {
                        //We have an existing entity. Update its Definition and Name from the push event...KB
                        cachedEntity.Definition = updatedEntity.Definition;
                        cachedEntity.Name = updatedEntity.Name;
                    }
                    postUpdateCallback(cachedEntity, updatedEntity);
                }
                else {
                    actionString = 'added';
                    //We have a new entity. Insert it into the cache...KB
                    updatedEntity._State = EntityStates.Unmodified;
                    cacheRef.add(updatedEntity[`${entityType}ID`], updatedEntity, updatedEntity.UID);
                    postNewCallback(updatedEntity);
                }
                logEvent(updatedEntity, event.updatedBy, entityType, actionString);
            });
        };

        let logEvent = (updatedEntity, updatedBy, entityType, actionString) => {
            let entityName = (updatedEntity.EntityName != null) ? "a pending change for " + updatedEntity.EntityName : updatedEntity.Definition.Name;
            console.log(`${entityType.toUpperCase()} CHANGED: ${updatedBy} has ${actionString} ${entityName}. Downloaded updated ${entityType}.`);
        };

        // if designer is not fully loaded yet, then we need to allow it to update and then try again.
        let retryUpdateConflictNotifications = () => {
            if (api.isMobileDevice()) {
                console.warn(`Received pending change entities and designer UI is not available, but running on a mobile device; will NOT retry to update designer UI.`);
                return;
            }

            setTimeout(() => {
                if (app.designer == null || app.designer.rails == null) {
                    console.warn(`Received pending change entities but designer UI is not available; will retry to update designer UI.`);
                    retryUpdateConflictNotifications();
                    return;
                }
                app.designer.rails.settingsRail.updateConflictNotifications();
            }, 1);
        };

        switch(event.entityType) {
            case 'View':
                // The client needs to merge the new and/or updated views into it's cache (including originalViews for reverting)... EK

                // Populate the array of current open forms that may become out of sync
                let editFormsOutOfSync = [],
                    openForms = new Map();
                for (let openMVC of app.dv.cache.mvcs.Values) {
                    if (openMVC.drivingView) {
                        openForms.set(openMVC.drivingView.ViewID, openMVC.drivingView);
                    }
                    for (let subMVC of app.dv.mvc.getSubMVCsFromMVC(openMVC)) {
                        if (subMVC.drivingView) {
                            openForms.set(subMVC.drivingView.ViewID, subMVC.drivingView);
                        }
                    }
                }

                event.entities.forEach((updatedView) => {
                    let cacheView = c.views.allViews.getVal(updatedView.ViewID),
                        cacheOriginalView = c.views.allOriginalViews.getVal(updatedView.ViewID);
                    if (cacheView) {
                        //We have an existing entity. Update its Definition and Name from the push event...KB
                        if (cacheOriginalView) {
                            //There is already an original view in the list (which means we have a pending change).  Persist this pending view and let the designer know of a potential conflict...EK
                            cacheOriginalView.Definition = updatedView.Definition;
                            cacheOriginalView.Name = updatedView.Name;
                        }
                        else {
                            cacheView.Definition = updatedView.Definition;
                            cacheView.Name = updatedView.Name;
                        }

                        let openForm = openForms.get(cacheView.ViewID);
                        if (openForm) {
                            // We just updated a currently open form, flag it as out of sync to notify on designer toggle
                            openForm.clientProperties.isOutOfSync = true;
                        }

                        // Check if any of our current editing views are now out of sync
                        let editingForm = app.designer.monitor.editingViews.getVal(updatedView.ViewID);
                        if (editingForm) {
                            editFormsOutOfSync.push(editingForm.Definition.Name);
                        }
                    }
                    else {
                        //We have a new entity. Insert it into the cache...KB
                        updatedView._State = EntityStates.Unmodified;
                        applyViewClientProps(updatedView);
                        c.views.allViews.add(updatedView.ViewID, updatedView, updatedView.UID);
                    }
                    console.log(`FORM CHANGED: ${event.updatedBy} has changed ${updatedView.Definition.Name}. Downloaded updated form.`);
                });

                //Notify the user of any out of sync forms
                app.designer.showOutOfSyncFormNotification(editFormsOutOfSync);
                break;
            case 'Property':
                properties.mergeCache(event.entities,
                    (entity, action) => {
                        logEvent(entity, event.updatedBy, event.entityType, action);
                    },
                    (entity, action) => {
                        logEvent(entity, event.updatedBy, event.entityType, action);
                    });
                break;

            case 'Category':
                categories.mergeCache(event.entities,
                    (entity, action) => {
                        logEvent(entity, event.updatedBy, event.entityType, action);
                    },
                    (entity, action) => {
                        logEvent(entity, event.updatedBy, event.entityType, action);
                    });
                break;

            case 'RecordType':
                recordTypes.mergeCache(event.entities,
                    (entity, action) => {
                        logEvent(entity, event.updatedBy, event.entityType, action);
                    },
                    (entity, action) => {
                        logEvent(entity, event.updatedBy, event.entityType, action);
                    });
                break;
            case 'Style':
                mergeEntitiesToCache(event.entities, c.styles.allStyles, EntityTypes.Style, applyStyleClientProps, applyStyleClientProps);
                break;
            case 'ValueStatus':
                mergeEntitiesToCache(event.entities, c.valueStatuses.allValueStatuses, EntityTypes.ValueStatus, (newStatus) => {
                    applyVSClientProps(newStatus);
                    c.valueStatuses.valueStatusByValue.add(newStatus.StatusValue, newStatus, newStatus.UID);
                });
                break;
            case 'Tag':
                mergeEntitiesToCache(event.entities, c.tags.allTags, EntityTypes.Tag, applyTagClientProps);
                break;
            case 'TagLink':
                let updateLinkedEntityClientProperties = (tagLink) => {
                    let linkedEntity, foundType = true;
                    switch (tagLink.Table) {
                        case 'view':
                            linkedEntity = c.views.allViews.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'property':
                            linkedEntity = c.properties.allProperties.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'category':
                            linkedEntity = c.categories.allCategories.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'recordtype':
                            linkedEntity = c.recordTypes.allRecordTypes.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'style':
                            linkedEntity = c.styles.allStyles.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'valuestatus':
                            linkedEntity = c.valueStatuses.allValueStatuses.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'tag':
                            linkedEntity = c.tags.allTags.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'security':
                            linkedEntity = c.securities.allSecurities.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'securitygroup':
                            linkedEntity = c.securityGroups.allSecurityGroups.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'securitygrouplookup':
                            linkedEntity = c.securityGroupLookups.allSecurityGroupLookups.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'package':
                            linkedEntity = c.packages.allPackages.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'extension - action':
                            linkedEntity = c.extensions.allActions.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'extension - expression':
                            linkedEntity = c.extensions.allExpressions.getVal({UID: tagLink.EntityUID});
                            break;
                        case 'extension - endpoint':
                            linkedEntity = c.extensions.allCustomEndpoints.getVal({UID: tagLink.EntityUID});
                            break;
                        default:
                            console.warn('A tag link is attempting to be added to an unknown entity type');
                            foundType = false;
                    }
                    if (!linkedEntity && foundType) {
                        console.warn('A tag link was added to an unknown entity');
                    }
                    else {
                        c.organizeTagsForEntity(linkedEntity);
                    }
                };
                mergeEntitiesToCache(event.entities, c.tags.allTagLinks, EntityTypes.TagLink, updateLinkedEntityClientProperties, updateLinkedEntityClientProperties);
                break;
            case 'Security':
                mergeEntitiesToCache(event.entities, c.securities.allSecurities, EntityTypes.Security);
                break;
            case 'SecurityGroup':
                mergeEntitiesToCache(event.entities, c.securityGroups.allSecurityGroups, EntityTypes.SecurityGroup, applySGClientProps);
                break;
            case 'SecurityGroupLookup':
                mergeEntitiesToCache(event.entities, c.securityGroupLookups.allSecurityGroupLookups, EntityTypes.SecurityGroupLookup);
                break;
            case 'Package':
                mergeEntitiesToCache(event.entities, c.packages.allPackages, EntityTypes.Package, applyPackageClientProps);
                break;
            case 'Extension':
                event.entities.forEach((updatedEntity) => {
                    let cachedEntity;
                    switch (updatedEntity.Type) {
                        case 'Action':
                            cachedEntity = c.extensions.allActions.getVal(updatedEntity);
                            if (cachedEntity) {
                                cachedEntity.Definition = updatedEntity.Definition;
                                cachedEntity.Name = updatedEntity.Name;
                            }
                            else {
                                updatedEntity._State = EntityStates.Unmodified;
                                applyActionClientProperties(updatedEntity);
                                cachedEntity = updatedEntity;
                            }
                            // we need to re-register the extension..WR
                            app.dv.cache.extensions.registerAction(cachedEntity);
                            break;
                        case 'Expression':
                            cachedEntity = c.extensions.allExpressions.getVal(updatedEntity);
                            if (cachedEntity) {
                                cachedEntity.Definition = updatedEntity.Definition;
                                cachedEntity.Name = updatedEntity.Name;
                            }
                            else {
                                updatedEntity._State = EntityStates.Unmodified;
                                applyExpressionClientProperties(updatedEntity);
                                cachedEntity = updatedEntity;
                            }
                            app.dv.cache.extensions.registerExpression(cachedEntity);
                            break;
                        case 'Endpoint':
                            cachedEntity = c.extensions.allCustomEndpoints.getVal(updatedEntity);
                            if (cachedEntity) {
                                cachedEntity.Definition = updatedEntity.Definition;
                                cachedEntity.Name = updatedEntity.Name;
                            }
                            else {
                                updatedEntity._State = EntityStates.Unmodified;
                                applyEndpointClientProperties(updatedEntity);
                                cachedEntity = updatedEntity;
                                c.extensions.allCustomEndpoints.add(cachedEntity.ExtensionID, cachedEntity, cachedEntity.UID);
                            }
                            // We are not going to register the endpoint here so that designers are not confused since endpoint entities will not be pulled down by non-designers.
                            // if (cachedEntity.Definition.IsPublished) {
                            //     app.dv.cache.extensions.registerEndpoint(cachedEntity);
                            // }
                            break;
                        default:
                            console.warn(`Attempted to merge unknown extension type ${updatedEntity.Type} into cache`);
                            return;
                    }
                    console.log(`${updatedEntity.Type.toUpperCase()} CHANGED: ${event.updatedBy} has changed ${updatedEntity.Name}. Downloaded updated ${updatedEntity.Type}.`);
                });
                break;
            case "PendingChange":
                //Update the ChangedEntityType property when the entity is created and updated...eg
                mergeEntitiesToCache(event.entities, c.pendingChanges.allPendingChanges, EntityTypes.PendingChange, applyPCClientProps);

                //Update the rail notifications...eg
                retryUpdateConflictNotifications();

                break;
            default:
                console.warn(`Attempted to merge unknown entity type ${event.entityType} into cache`);
                break;
        }
    }
};
